import { useLayoutEffect, useState } from 'react';

const memoryCache = new Map<string, string>();

export type CachedImageProps = {
  src: string;
  alt: string;
  testId: string;
  className?: string;
  style?: React.CSSProperties;
  blur?: string;
};

export const CachedImage: React.FC<CachedImageProps> = ({
  src,
  alt,
  testId,
  className,
  style,
  blur = '10px',
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useLayoutEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      if (memoryCache.has(src)) {
        setImageUrl(memoryCache.get(src)!);
        return;
      }

      try {
        const response = await fetch(src);
        if (!response.ok) {
          throw new Error('Image request failed');
        }
        const blob = await response.blob();
        const objectURL = URL.createObjectURL(blob);
        memoryCache.set(src, objectURL);
        if (isMounted) {
          setImageUrl(objectURL);
        }
      } catch {
        if (isMounted) setImageUrl('/icons/3d-icons/not-found.png');
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [src]);

  return imageUrl ? (
    <img
      className={className}
      src={imageUrl}
      alt={alt}
      data-test-id={`${testId}-container`}
      style={{
        filter: isLoaded ? 'none' : `blur(${blur})`,
        transition: 'filter 0.2s ease-out',
        ...(style ?? {}),
      }}
      onLoad={() => setIsLoaded(true)}
      onError={() => setIsLoaded(true)}
    />
  ) : null;
};
